import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Space, Modal, Form, Input, InputNumber, Select, message, DatePicker, Typography, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { apiRequest } from '../../utils/api';
import { getApiUrl } from '../../config/api';
import dayjs from 'dayjs';
import jalaliday from 'jalali-dayjs';

dayjs.extend(jalaliday);
const { Title, Text } = Typography;
const { Option } = Select;

const toLatinDigits = (value) => {
  if (!value) return value;
  return String(value).replace(/[۰-۹]/g, w => String.fromCharCode(w.charCodeAt(0) - 1728));
};

const STATUS_CONFIG = {
  in_vault: { text: 'نزد صندوق', color: 'warning' },
  deposited: { text: 'در جریان وصول', color: 'processing' },
  cleared: { text: 'پاس‌شده', color: 'success' },
  bounced: { text: 'برگشت‌خورده', color: 'error' },
  returned: { text: 'عودت داده‌شده', color: 'default' }
};

const ChequeVault = () => {
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();

  const fetchCheques = async () => {
    try {
      setLoading(true);
      // Assuming a GET endpoint exists as standard REST
      const res = await apiRequest(getApiUrl('financial/cheques'), { method: 'GET' });
      const chqs = res?.data?.cheques || res?.data; setCheques(Array.isArray(chqs) ? chqs : []);
    } catch (error) {
      console.error(error);
      // Fallback empty if endpoint doesn't exist yet
      setCheques([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCheques();
  }, []);

  const handleRegister = async (values) => {
    try {
      // Ensure numeric conversion and proper date formatting
      const payload = {
        ...values,
        client_id: values.client_id ? Number(toLatinDigits(values.client_id)) : null,
        deal_id: values.deal_id ? Number(toLatinDigits(values.deal_id)) : null,
        deal_installment_id: values.deal_installment_id ? Number(toLatinDigits(values.deal_installment_id)) : null,
        amount: Number(toLatinDigits(values.amount)),
        sayad_number: toLatinDigits(values.sayad_number),
        cheque_number: toLatinDigits(values.cheque_number),
        account_number: toLatinDigits(values.account_number),
        issuer_national_id: toLatinDigits(values.issuer_national_id),
        bank_account_id: values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null,
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
      };
      await financialApi.registerCheque(payload);
      message.success('چک با موفقیت ثبت شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت چک');
    }
  };

  const handleChangeStatus = async (values) => {
    if (!selectedCheque) return;
    try {
      const payload = { status: values.status };
      if (values.status === 'cleared') {
        payload.bank_account_id = values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null;
      }
      await financialApi.changeChequeStatus(selectedCheque.id, payload);
      message.success('وضعیت چک تغییر کرد');
      setIsStatusModalVisible(false);
      statusForm.resetFields();
      setSelectedCheque(null);
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تغییر وضعیت');
    }
  };

  const columns = [
    {
      title: 'شماره چک',
      dataIndex: 'cheque_number',
      key: 'cheque_number',
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatPriceToman(amount),
    },
    {
      title: 'شماره صیاد',
      dataIndex: 'sayad_number',
      key: 'sayad_number',
    },
    {
      title: 'بانک',
      dataIndex: 'bank_name',
      key: 'bank_name',
    },
    {
      title: 'تاریخ سررسید',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => {
        if (!date) return '-';
        const d = dayjs(date);
        return <span dir="ltr">{d.isValid() ? d.calendar('jalali').format('YYYY/MM/DD') : '-'}</span>;
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = STATUS_CONFIG[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EditOutlined />}
          onClick={() => {
            setSelectedCheque(record);
            statusForm.setFieldsValue({ status: record.status });
            setIsStatusModalVisible(true);
          }}
        >
          تغییر وضعیت
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              خزانه چک صیادی
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              مدیریت چک‌های دریافتی، ثبت و تغییر وضعیت آن‌ها
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setIsModalVisible(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            ثبت چک جدید
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={cheques}
          rowKey="id"
          loading={loading}
          className="custom-table dark:bg-gray-800 rounded-2xl shadow-sm"
          locale={{ emptyText: 'هیچ چکی در خزانه یافت نشد' }}
        />

        <Modal
          title="ثبت چک صیادی جدید"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form layout="vertical" form={form} onFinish={handleRegister}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="client_id" label="مشتری (ID)" rules={[{ required: true }]}>
                  <InputNumber className="w-full" placeholder="شناسه مشتری" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_id" label="معامله (ID)">
                  <InputNumber className="w-full" placeholder="شناسه معامله" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_installment_id" label="قسط (ID)">
                  <InputNumber className="w-full" placeholder="شناسه قسط" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="amount" label="مبلغ (تومان)" rules={[{ required: true }]}>
                  <InputNumber 
                    className="w-full" 
                    placeholder="مثلا 10000000"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sayad_number" label="شماره صیاد" rules={[{ required: true }]}>
                  <Input placeholder="۱۶ رقم صیادی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cheque_number" label="شماره چک">
                  <Input placeholder="سریال چک" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="bank_name" label="نام بانک" rules={[{ required: true }]}>
                  <Input placeholder="مثلا بانک ملت" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="branch_name" label="نام شعبه">
                  <Input placeholder="مثلا شعبه مرکزی" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="account_number" label="شماره حساب">
                  <Input placeholder="شماره حساب صادرکننده" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="issuer_name" label="نام صادرکننده" rules={[{ required: true }]}>
                  <Input placeholder="نام و نام خانوادگی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="issuer_national_id" label="کد ملی صادرکننده">
                  <Input placeholder="کد ملی ۱۰ رقمی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="due_date" label="تاریخ سررسید" rules={[{ required: true }]}>
                  <DatePicker className="w-full" placeholder="انتخاب تاریخ" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bank_account_id" label="حساب مقصد (ID)">
                  <InputNumber className="w-full" placeholder="شناسه حساب بانکی" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="توضیحات">
                  <Input.TextArea placeholder="یادداشت‌های اضافی..." />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item className="mb-0 text-left">
              <Button onClick={() => setIsModalVisible(false)} className="ml-2">انصراف</Button>
              <Button type="primary" htmlType="submit" className="bg-blue-600">ثبت</Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="تغییر وضعیت چک"
          open={isStatusModalVisible}
          onCancel={() => {
            setIsStatusModalVisible(false);
            setSelectedCheque(null);
          }}
          footer={null}
        >
          <Form layout="vertical" form={statusForm} onFinish={handleChangeStatus}>
            <Form.Item name="status" label="وضعیت جدید" rules={[{ required: true }]}>
              <Select>
                <Option value="in_vault">نزد صندوق</Option>
                <Option value="deposited">واگذار به بانک / در جریان وصول</Option>
                <Option value="cleared">پاس‌شده</Option>
                <Option value="bounced">برگشت‌خورده</Option>
                <Option value="returned">عودت داده‌شده</Option>
              </Select>
            </Form.Item>
            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.status !== curr.status}
            >
              {({ getFieldValue }) => {
                const status = getFieldValue('status');
                if (status === 'cleared') {
                  return (
                    <Form.Item name="bank_account_id" label="شناسه حساب بانکی برای پاس‌شدن" rules={[{ required: true }]}>
                      <InputNumber className="w-full" placeholder="شناسه حساب بانکی مقصد" />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>
            <Form.Item className="mb-0 text-left">
              <Button onClick={() => setIsStatusModalVisible(false)} className="ml-2">انصراف</Button>
              <Button type="primary" htmlType="submit" className="bg-blue-600">اعمال تغییر</Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ChequeVault;
